/**
 * Study Grouping Utilities
 * 
 * Functions for organizing studies hierarchically by symbol and timeframe,
 * calculating study age/staleness, and parsing study metadata.
 */

import type { Study } from "@/types/api";
import { differenceInDays, parseISO } from "date-fns";
import { normalizeTimeframe } from "@/utils/normalizeTimeframe";

// =============================================================================
// Types
// =============================================================================

export type FreshnessStatus = "fresh" | "aging" | "stale";

export interface ParsedStudyMetadata {
  symbol: string;
  timeframe: string;
  version: number;
  dateStr: string | null;
  isSampleData: boolean;
}

export interface StudyWithMetadata extends Study {
  metadata: ParsedStudyMetadata;
  ageInDays: number;
  freshness: FreshnessStatus;
}

export interface TimeframeGroup {
  timeframe: string;
  displayTimeframe: string;
  studies: StudyWithMetadata[];
  studyCount: number;
  worstFreshness: FreshnessStatus;
  bestValue: number | null;
}

export interface SymbolGroup {
  symbol: string;
  displaySymbol: string;
  timeframes: TimeframeGroup[];
  totalStudies: number;
  worstFreshness: FreshnessStatus;
}

export interface GroupedStudies {
  groups: SymbolGroup[];
  allSymbols: string[];
  allTimeframes: string[];
  totalStudies: number;
  freshCount: number;
  agingCount: number;
  staleCount: number;
}

export type ParallelFilterMode = "all" | "parallel" | "standalone";

// =============================================================================
// Constants
// =============================================================================

const FRESHNESS_THRESHOLDS = {
  FRESH_MAX_DAYS: 14,
  AGING_MAX_DAYS: 30,
};

// Common timeframe mappings for display
const TIMEFRAME_DISPLAY_MAP: Record<string, string> = {
  // Legacy aliases → canonical
  "minute_1": "1m",
  "minute_2": "2m",
  "minute_3": "3m",
  "minute_5": "5m",
  "minute_10": "10m",
  "minute_15": "15m",
  "minute_30": "30m",
  "hour_1": "1H",
  "hour_2": "2H",
  "hour_4": "4H",
  "day_1": "D",
  "d1": "D",
  "1d": "D",
  "h1": "1H",
  "h2": "2H",
  "h4": "4H",
  "1w": "W",
  "m30": "30m",
  "m15": "15m",
  "m5": "5m",
  "m3": "3m",
  "m2": "2m",
  "m10": "10m",
  "m1": "1m",
  "w1": "W",
  "week_1": "W",
  // Canonical values map to themselves for lookup consistency
  "1s": "1s", "5s": "5s", "15s": "15s", "30s": "30s",
  "1m": "1m", "2m": "2m", "3m": "3m", "5m": "5m", "10m": "10m", "15m": "15m", "30m": "30m",
  "1H": "1H", "2H": "2H", "3H": "3H", "4H": "4H", "6H": "6H", "8H": "8H", "12H": "12H", "16H": "16H", "23H": "23H",
  "D": "D", "2D": "2D", "4D": "4D", "W": "W", "M": "M",
};

// Timeframe sort order (smallest to largest)
const TIMEFRAME_SORT_ORDER: Record<string, number> = {
  "1s": 1, "5s": 2, "15s": 3, "30s": 4,
  "1m": 5, "2m": 6, "3m": 7, "5m": 8, "10m": 9, "15m": 10, "30m": 11,
  "1H": 12, "2H": 13, "3H": 14, "4H": 15, "6H": 16, "8H": 17, "12H": 18, "16H": 19, "23H": 20,
  "D": 21, "2D": 22, "4D": 23, "W": 24, "M": 25,
};

// =============================================================================
// Parsing Functions
// =============================================================================

/**
 * Parse a study name to extract symbol, timeframe, and version info.
 * 
 * Examples:
 * - "sample_btc_usd_d1_20250622" -> { symbol: "BTC/USD", timeframe: "D", version: 1 }
 * - "BTC_USD_4H_v3" -> { symbol: "BTC/USD", timeframe: "4H", version: 3 }
 * - "my_custom_study" -> { symbol: "Unknown", timeframe: "Unknown", version: 1 }
 */
export function parseStudyName(studyName: string): ParsedStudyMetadata {
  const isSampleData = studyName.toLowerCase().startsWith("sample_");
  const cleanName = isSampleData 
    ? studyName.slice(7) // Remove "sample_" prefix
    : studyName;
  
  // Try to extract symbol (usually first part, like "btc_usd" or "eth_usdt")
  const symbolPatterns = [
    /^([a-zA-Z]+)_([a-zA-Z]+)/i,  // btc_usd, eth_usdt
    /^([a-zA-Z]+)([a-zA-Z]+)/i,   // btcusd
  ];
  
  let symbol = "Unknown";
  let remaining = cleanName;
  
  for (const pattern of symbolPatterns) {
    const match = cleanName.match(pattern);
    if (match && match[1] && match[2]) {
      symbol = `${match[1].toUpperCase()}/${match[2].toUpperCase()}`;
      remaining = cleanName.slice(match[0].length).replace(/^_/, "");
      break;
    }
  }
  
  // Try to extract timeframe
  const timeframePatterns = [
    /^(d1|h1|h2|h4|m30|m15|m10|m5|m3|m2|m1|1d|w1|1w)/i,
    /^(day_1|hour_1|hour_2|hour_4|minute_30|minute_15|minute_10|minute_5|minute_3|minute_2|minute_1|week_1)/i,
    /^(23[hH]|1[0-9][hH]|[1-9][hH]|4[dD]|2[dD]|30[ms]|15[ms]|10m|[1-9]m|[1-9]s|1[05]s|30s|[dDwWmM])(?=[_\s]|$)/,
  ];
  
  let timeframe = "Unknown";
  
  for (const pattern of timeframePatterns) {
    const match = remaining.match(pattern);
    if (match && match[1]) {
      const rawTf = match[1].toLowerCase();
      timeframe = TIMEFRAME_DISPLAY_MAP[rawTf] || rawTf.toUpperCase();
      remaining = remaining.slice(match[0].length).replace(/^_/, "");
      break;
    }
  }
  
  // Try to extract date
  const dateMatch = remaining.match(/(\d{8})/);
  const dateStr = dateMatch && dateMatch[1] ? dateMatch[1] : null;
  
  // Try to extract version
  const versionMatch = remaining.match(/v(\d+)/i);
  const version = versionMatch && versionMatch[1] ? parseInt(versionMatch[1], 10) : 1;
  
  return {
    symbol,
    timeframe,
    version,
    dateStr,
    isSampleData,
  };
}

/**
 * Parse a study timestamp into a sortable epoch value.
 * Handles common Postgres timestamp formats and invalid strings.
 */
export function parseStudyTimestamp(value?: string | null): number | null {
  if (!value) return null;

  let normalized = value.includes(" ") ? value.replace(" ", "T") : value;
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  normalized = normalized.replace(/([+-]\d{2})$/, "$1:00");

  const time = new Date(normalized).getTime();
  return Number.isFinite(time) ? time : null;
}

/**
 * Calculate study age in days from completion or creation date.
 */
export function calculateStudyAge(study: Study): number {
  const referenceDate = study.completed_at || study.created_at;
  if (!referenceDate) return 0;
  
  try {
    const date = parseISO(referenceDate);
    return differenceInDays(new Date(), date);
  } catch {
    return 0;
  }
}

/**
 * Determine freshness status based on age in days.
 */
export function getFreshnessStatus(ageInDays: number): FreshnessStatus {
  if (ageInDays <= FRESHNESS_THRESHOLDS.FRESH_MAX_DAYS) {
    return "fresh";
  }
  if (ageInDays <= FRESHNESS_THRESHOLDS.AGING_MAX_DAYS) {
    return "aging";
  }
  return "stale";
}

/**
 * Get the worst freshness status from a list of statuses.
 */
export function getWorstFreshness(statuses: FreshnessStatus[]): FreshnessStatus {
  if (statuses.includes("stale")) return "stale";
  if (statuses.includes("aging")) return "aging";
  return "fresh";
}

/**
 * Format timeframe for display with proper sorting key.
 */
export function getTimeframeDisplayName(timeframe: string): string {
  const lower = timeframe.toLowerCase();
  return TIMEFRAME_DISPLAY_MAP[lower] || timeframe;
}

/**
 * Get sort order for a timeframe (for ordering groups).
 */
export function getTimeframeSortOrder(displayTimeframe: string): number {
  return TIMEFRAME_SORT_ORDER[displayTimeframe] ?? 100;
}

// =============================================================================
// Grouping Functions
// =============================================================================

/**
 * Enrich a study with parsed metadata and freshness info.
 */
export function enrichStudyWithMetadata(study: Study): StudyWithMetadata {
  const parsedMetadata = parseStudyName(study.study_name || study.study_id);

  // Prefer stored study.timeframe over name-parsed timeframe
  let timeframe = parsedMetadata.timeframe;
  if (study.timeframe && study.timeframe.trim()) {
    const normalized = normalizeTimeframe(study.timeframe);
    // Use normalized if recognized, otherwise preserve raw to avoid data loss
    timeframe = normalized ?? study.timeframe;
  }

  const metadata = { ...parsedMetadata, timeframe };
  const ageInDays = calculateStudyAge(study);
  const freshness = getFreshnessStatus(ageInDays);

  return {
    ...study,
    metadata,
    ageInDays,
    freshness,
  };
}

/**
 * Group studies hierarchically by symbol and timeframe.
 */
export function groupStudiesBySymbolAndTimeframe(
  studies: Study[]
): GroupedStudies {
  // Enrich all studies with metadata
  const enrichedStudies = studies.map(enrichStudyWithMetadata);
  
  // Group by symbol first
  const symbolMap = new Map<string, StudyWithMetadata[]>();
  
  for (const study of enrichedStudies) {
    const symbol = study.metadata.symbol;
    const existing = symbolMap.get(symbol) || [];
    existing.push(study);
    symbolMap.set(symbol, existing);
  }
  
  // Build hierarchical structure
  const groups: SymbolGroup[] = [];
  const allSymbols = new Set<string>();
  const allTimeframes = new Set<string>();
  let freshCount = 0;
  let agingCount = 0;
  let staleCount = 0;
  
  for (const [symbol, symbolStudies] of symbolMap) {
    allSymbols.add(symbol);
    
    // Group by timeframe within symbol
    const timeframeMap = new Map<string, StudyWithMetadata[]>();
    
    for (const study of symbolStudies) {
      const tf = study.metadata.timeframe;
      const existing = timeframeMap.get(tf) || [];
      existing.push(study);
      timeframeMap.set(tf, existing);
      allTimeframes.add(tf);
      
      // Count freshness
      if (study.freshness === "fresh") freshCount++;
      else if (study.freshness === "aging") agingCount++;
      else staleCount++;
    }
    
    // Build timeframe groups
    const timeframes: TimeframeGroup[] = [];
    
    for (const [timeframe, tfStudies] of timeframeMap) {
      // Sort studies by version (newest first) or by best_value
      const sortedStudies = [...tfStudies].sort((a, b) => {
        // By version descending
        if (a.metadata.version !== b.metadata.version) {
          return b.metadata.version - a.metadata.version;
        }
        // Then by best_value descending
        return (b.best_value ?? 0) - (a.best_value ?? 0);
      });
      
      const displayTimeframe = getTimeframeDisplayName(timeframe);
      const worstFreshness = getWorstFreshness(
        sortedStudies.map((s) => s.freshness)
      );
      const bestValue = sortedStudies.reduce(
        (max, s) => Math.max(max, s.best_value ?? -Infinity),
        -Infinity
      );
      
      timeframes.push({
        timeframe,
        displayTimeframe,
        studies: sortedStudies,
        studyCount: sortedStudies.length,
        worstFreshness,
        bestValue: bestValue === -Infinity ? null : bestValue,
      });
    }
    
    // Sort timeframes by sort order
    timeframes.sort(
      (a, b) =>
        getTimeframeSortOrder(a.displayTimeframe) -
        getTimeframeSortOrder(b.displayTimeframe)
    );
    
    const totalStudies = symbolStudies.length;
    const worstFreshness = getWorstFreshness(
      symbolStudies.map((s) => s.freshness)
    );
    
    groups.push({
      symbol,
      displaySymbol: symbol,
      timeframes,
      totalStudies,
      worstFreshness,
    });
  }
  
  // Sort groups by symbol name
  groups.sort((a, b) => a.symbol.localeCompare(b.symbol));
  
  return {
    groups,
    allSymbols: Array.from(allSymbols).sort(),
    allTimeframes: Array.from(allTimeframes).sort(
      (a, b) =>
        getTimeframeSortOrder(getTimeframeDisplayName(a)) -
        getTimeframeSortOrder(getTimeframeDisplayName(b))
    ),
    totalStudies: enrichedStudies.length,
    freshCount,
    agingCount,
    staleCount,
  };
}

// =============================================================================
// Filter Functions
// =============================================================================

export interface StudyFilters {
  symbols: string[];
  timeframes: string[];
  freshness: FreshnessStatus[];
  searchQuery: string;
  parallelMode: ParallelFilterMode;
  // AIE-539: "Show labeled only" — layers on top of the type filter.
  showLabeledOnly: boolean;
  // AIE-539: specific label strings to filter by (OR). Non-empty takes precedence
  // over showLabeledOnly (a precise selection is inherently "labeled only").
  selectedLabels: string[];
}

/** A distinct user label with its category color and how many studies carry it. */
export interface LabelSummary {
  label: string;
  color: string;
  count: number;
}

/**
 * Derive the distinct set of user labels from a list of studies (AIE-539), with
 * each label's category color and usage count, ordered most-used first (ties
 * broken alphabetically). Untagged / whitespace-only labels are ignored.
 */
export function getDistinctLabels(studies: Study[]): LabelSummary[] {
  const byLabel = new Map<string, LabelSummary>();
  for (const study of studies) {
    const label = study.user_label?.trim();
    if (!label) continue;
    const existing = byLabel.get(label);
    if (existing) {
      existing.count += 1;
    } else {
      byLabel.set(label, {
        label,
        color: study.user_label_color?.trim() || "blue",
        count: 1,
      });
    }
  }
  return [...byLabel.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
}

/**
 * Filter grouped studies based on filter criteria.
 */
export function filterGroupedStudies(
  grouped: GroupedStudies,
  filters: StudyFilters
): GroupedStudies {
  const { symbols, timeframes, freshness, searchQuery, parallelMode, showLabeledOnly, selectedLabels } =
    filters;
  const searchLower = searchQuery.toLowerCase();
  const selectedLabelSet = new Set(selectedLabels);
  
  let freshCount = 0;
  let agingCount = 0;
  let staleCount = 0;
  
  const filteredGroups = grouped.groups
    .filter((group) => {
      // Filter by symbol if specified
      if (symbols.length > 0 && !symbols.includes(group.symbol)) {
        return false;
      }
      return true;
    })
    .map((group) => {
      // Filter timeframes within group
      const filteredTimeframes = group.timeframes
        .filter((tf) => {
          if (timeframes.length > 0 && !timeframes.includes(tf.timeframe)) {
            return false;
          }
          return true;
        })
        .map((tf) => {
          // Filter studies within timeframe
          const filteredStudies = tf.studies.filter((study) => {
            if (parallelMode === "parallel" && !study.batch_id) {
              return false;
            }
            if (parallelMode === "standalone" && study.batch_id) {
              return false;
            }
            // Filter by freshness
            if (freshness.length > 0 && !freshness.includes(study.freshness)) {
              return false;
            }
            // AIE-539: label filter. A specific label selection (OR, exact match)
            // takes precedence; otherwise "Show labeled only" keeps any tagged study.
            const studyLabel = study.user_label?.trim();
            if (selectedLabelSet.size > 0) {
              if (!studyLabel || !selectedLabelSet.has(studyLabel)) {
                return false;
              }
            } else if (showLabeledOnly && !studyLabel) {
              return false;
            }
            // Filter by search query (also matches the user's label text — AIE-539)
            if (searchLower) {
              const nameMatch = study.study_name?.toLowerCase().includes(searchLower);
              const idMatch = study.study_id.toLowerCase().includes(searchLower);
              const labelMatch = study.user_label?.toLowerCase().includes(searchLower) ?? false;
              if (!nameMatch && !idMatch && !labelMatch) {
                return false;
              }
            }
            return true;
          });
          
          // Count freshness
          for (const s of filteredStudies) {
            if (s.freshness === "fresh") freshCount++;
            else if (s.freshness === "aging") agingCount++;
            else staleCount++;
          }
          
          return {
            ...tf,
            studies: filteredStudies,
            studyCount: filteredStudies.length,
            worstFreshness: getWorstFreshness(
              filteredStudies.map((s) => s.freshness)
            ),
          };
        })
        .filter((tf) => tf.studyCount > 0);
      
      return {
        ...group,
        timeframes: filteredTimeframes,
        totalStudies: filteredTimeframes.reduce((sum, tf) => sum + tf.studyCount, 0),
        worstFreshness: getWorstFreshness(
          filteredTimeframes.map((tf) => tf.worstFreshness)
        ),
      };
    })
    .filter((group) => group.totalStudies > 0);
  
  return {
    ...grouped,
    groups: filteredGroups,
    totalStudies: filteredGroups.reduce((sum, g) => sum + g.totalStudies, 0),
    freshCount,
    agingCount,
    staleCount,
  };
}

// =============================================================================
// Display Helpers
// =============================================================================

/**
 * Get freshness badge color class.
 */
export function getFreshnessColorClass(freshness: FreshnessStatus): string {
  switch (freshness) {
    case "fresh":
      return "bg-green-500/20 text-green-400 border-green-500/30";
    case "aging":
      return "bg-amber-500/20 text-amber-400 border-amber-500/30";
    case "stale":
      return "bg-red-500/20 text-red-400 border-red-500/30";
  }
}

/**
 * Get freshness label text.
 */
export function getFreshnessLabel(freshness: FreshnessStatus): string {
  switch (freshness) {
    case "fresh":
      return "Fresh";
    case "aging":
      return "Aging";
    case "stale":
      return "Stale";
  }
}

/**
 * Get freshness description for tooltips.
 */
export function getFreshnessDescription(
  freshness: FreshnessStatus,
  ageInDays: number
): string {
  switch (freshness) {
    case "fresh":
      return `${ageInDays}d old - Results are current`;
    case "aging":
      return `${ageInDays}d old - Consider re-running soon`;
    case "stale":
      return `${ageInDays}d old - Re-run recommended`;
  }
}

/**
 * Format age for display.
 */
export function formatAge(ageInDays: number): string {
  if (ageInDays === 0) return "Today";
  if (ageInDays === 1) return "1d";
  if (ageInDays < 7) return `${ageInDays}d`;
  if (ageInDays < 30) return `${Math.floor(ageInDays / 7)}w`;
  return `${Math.floor(ageInDays / 30)}mo`;
}

// =============================================================================
// Parallel Study Grouping
// =============================================================================

/**
 * Status type for a group of parallel studies.
 */
export type ParallelGroupStatus = "queued" | "running" | "completed" | "failed" | "cancelled" | "paused" | "mixed";

/**
 * A group of parallel studies that share the same parallel_group_id (stored as batch_id in DB).
 * 
 * When users launch multiple parallel study instances, they are linked together
 * via a shared identifier. This interface represents that grouping for UI display.
 */
export interface ParallelStudyGroup {
  /** The shared identifier for all studies in this parallel group (maps to batch_id in DB) */
  parallel_group_id: string;
  /** Studies in this group, sorted by their index */
  studies: Study[];
  /** Total number of studies in this parallel group */
  parallel_total: number;
  /** The base study name (without _v1, _v2 suffix) */
  baseName: string;
  /** Best value across all studies in the group */
  bestValue: number | null;
  /** Study with the best value (winner) */
  bestStudy: Study | null;
  /** Overall status of the group (running if any running, completed if all done, etc.) */
  groupStatus: ParallelGroupStatus;
  /** Completion percentage (n_trials_completed / n_trials across all studies) */
  completionPercentage: number;
  
  // Legacy aliases for backward compatibility during transition
  /** @deprecated Use parallel_group_id instead */
  batch_id: string;
  /** @deprecated Use parallel_total instead */
  batch_total: number;
  /** @deprecated Use groupStatus instead */
  batchStatus: ParallelGroupStatus;
}

/**
 * Result of grouping studies by their parallel group.
 */
export interface GroupedParallelStudies {
  /** Parallel study groups */
  parallelGroups: ParallelStudyGroup[];
  /** Standalone studies (not part of any parallel group) */
  standalone: Study[];
  /** Total number of parallel groups */
  parallelGroupCount: number;
  /** Total number of standalone studies */
  standaloneCount: number;
}

/** @deprecated Use StudySortConfig instead */
export type StudySortOrder = "newest" | "original";

export type StudySortField = "updated" | "created";
export type StudySortDirection = "asc" | "desc";

export interface StudySortConfig {
  field: StudySortField;
  direction: StudySortDirection;
}

export type StudyDisplayItem =
  | {
      type: "parallel";
      group: ParallelStudyGroup;
      sortKey: number;
      orderIndex: number;
    }
  | {
      type: "standalone";
      study: Study;
      sortKey: number;
      orderIndex: number;
    };

// Legacy type aliases for backward compatibility
/** @deprecated Use ParallelStudyGroup instead */
export type BatchGroup = ParallelStudyGroup;
/** @deprecated Use GroupedParallelStudies instead */
export type GroupedStudiesWithBatches = GroupedParallelStudies & {
  /** @deprecated Use parallelGroups instead */
  batches: ParallelStudyGroup[];
  /** @deprecated Use parallelGroupCount instead */
  batchCount: number;
};

/**
 * Extract the base name from a parallel study name.
 * "my_study_v1" -> "my_study"
 * "my_study_v10" -> "my_study"
 * "my_study" -> "my_study"
 */
export function extractBaseName(studyName: string): string {
  // Remove _v1, _v2, ..., _v10 suffix if present
  return studyName.replace(/_v\d+$/, "");
}

/**
 * Determine the overall status for a group of parallel studies.
 */
function determineGroupStatus(studies: Study[]): ParallelGroupStatus {
  if (studies.length === 0) return "mixed";

  const statuses = new Set(studies.map((s) => s.status));

  // If all studies have the same status, return that status (except "deleted")
  if (statuses.size === 1) {
    const status = studies[0]?.status;
    // Handle "deleted" status by mapping to "cancelled"
    if (status === "deleted") return "cancelled";
    return status ?? "mixed";
  }

  // Priority-based status determination
  if (statuses.has("running")) return "running";
  if (statuses.has("queued")) return "running"; // Treat queued with others as running
  if (statuses.has("paused")) return "paused";
  if (statuses.has("failed") && statuses.has("completed")) return "mixed";
  if (statuses.has("failed")) return "failed";
  if (statuses.has("cancelled")) return "cancelled";

  return "mixed";
}

/**
 * Calculate the completion percentage across all studies in a parallel group.
 */
function calculateGroupCompletion(studies: Study[]): number {
  let totalTrials = 0;
  let completedTrials = 0;

  for (const study of studies) {
    totalTrials += study.n_trials || 0;
    completedTrials += study.n_trials_completed || 0;
  }

  if (totalTrials === 0) return 0;
  return Math.round((completedTrials / totalTrials) * 100);
}

/**
 * Group studies by their parallel group identifier for display.
 *
 * Studies with the same batch_id (parallel group ID) are grouped together.
 * Studies without a batch_id are returned as standalone.
 */
export function groupParallelStudies(studies: Study[]): GroupedParallelStudies {
  const groupMap = new Map<string, Study[]>();
  const standalone: Study[] = [];

  // Separate grouped vs standalone studies
  for (const study of studies) {
    if (study.batch_id) {
      const existing = groupMap.get(study.batch_id) || [];
      existing.push(study);
      groupMap.set(study.batch_id, existing);
    } else {
      standalone.push(study);
    }
  }

  // Build parallel study groups
  const parallelGroups: ParallelStudyGroup[] = [];

  for (const [groupId, groupStudies] of groupMap) {
    // Sort by index (batch_index in DB)
    const sortedStudies = [...groupStudies].sort(
      (a, b) => (a.batch_index ?? 0) - (b.batch_index ?? 0)
    );

    // Find the best study (highest best_value)
    let bestStudy: Study | null = null;
    let bestValue: number | null = null;

    for (const study of sortedStudies) {
      if (study.best_value !== undefined && study.best_value !== null) {
        if (bestValue === null || study.best_value > bestValue) {
          bestValue = study.best_value;
          bestStudy = study;
        }
      }
    }

    // Get base name from first study
    const firstStudy = sortedStudies[0];
    if (!firstStudy) continue; // Skip empty groups

    const baseName = extractBaseName(firstStudy.study_name);
    const parallelTotal = firstStudy.batch_total ?? sortedStudies.length;
    const groupStatus = determineGroupStatus(sortedStudies);

    parallelGroups.push({
      // New canonical names
      parallel_group_id: groupId,
      studies: sortedStudies,
      parallel_total: parallelTotal,
      baseName,
      bestValue,
      bestStudy,
      groupStatus,
      completionPercentage: calculateGroupCompletion(sortedStudies),
      // Legacy aliases for backward compatibility
      batch_id: groupId,
      batch_total: parallelTotal,
      batchStatus: groupStatus,
    });
  }

  // Sort groups by creation time (newest first)
  const getGroupCreatedAt = (group: ParallelStudyGroup) => {
    const times = group.studies
      .map((study) => parseStudyTimestamp(study.created_at))
      .filter((time): time is number => time !== null);
    if (times.length === 0) return 0;
    return Math.min(...times);
  };

  parallelGroups.sort((a, b) => {
    return getGroupCreatedAt(b) - getGroupCreatedAt(a);
  });

  return {
    parallelGroups,
    standalone,
    parallelGroupCount: parallelGroups.length,
    standaloneCount: standalone.length,
  };
}

export function buildStudyDisplayItems(
  studies: Study[],
  sortConfig: StudySortConfig | StudySortOrder = { field: "updated", direction: "desc" }
): StudyDisplayItem[] {
  const grouped = groupParallelStudies(studies);
  const displayItems: StudyDisplayItem[] = [];
  const studyIndex = new Map(
    studies.map((study, index) => [study.study_id, index])
  );

  // Handle legacy string sort order
  // "original" = preserve API order (sort by orderIndex only)
  // "newest" = sort by created_at descending
  const isLegacyOriginal = sortConfig === "original";
  const normalizedConfig: StudySortConfig =
    typeof sortConfig === "string"
      ? sortConfig === "newest"
        ? { field: "created", direction: "desc" }
        : { field: "created", direction: "desc" } // Not used when isLegacyOriginal
      : sortConfig;

  const getStudyTimestamp = (study?: Study | null) => {
    if (!study) return 0;
    const fieldKey = normalizedConfig.field === "updated" ? "updated_at" : "created_at";
    return parseStudyTimestamp(study[fieldKey]) ?? 0;
  };

  const getGroupTimestamp = (group: ParallelStudyGroup) => {
    const times = group.studies
      .map((study) => getStudyTimestamp(study))
      .filter((time): time is number => time !== null && time > 0);
    // For groups, use the most recent timestamp (max) for "updated" or earliest (min) for "created"
    if (times.length === 0) return 0;
    return normalizedConfig.field === "updated" ? Math.max(...times) : Math.min(...times);
  };

  for (const parallelGroup of grouped.parallelGroups) {
    const sortKey = getGroupTimestamp(parallelGroup);
    const orderIndex = parallelGroup.studies.reduce((minIndex, study) => {
      const index = studyIndex.get(study.study_id) ?? Number.MAX_SAFE_INTEGER;
      return Math.min(minIndex, index);
    }, Number.MAX_SAFE_INTEGER);

    displayItems.push({
      type: "parallel",
      group: parallelGroup,
      sortKey,
      orderIndex,
    });
  }

  for (const study of grouped.standalone) {
    const sortKey = getStudyTimestamp(study);
    const orderIndex =
      studyIndex.get(study.study_id) ?? Number.MAX_SAFE_INTEGER;

    displayItems.push({
      type: "standalone",
      study,
      sortKey,
      orderIndex,
    });
  }

  // Sort based on configuration
  if (isLegacyOriginal) {
    // Legacy "original" behavior: preserve API order using orderIndex only
    displayItems.sort((a, b) => a.orderIndex - b.orderIndex);
  } else {
    // Sort by the configured field and direction
    displayItems.sort((a, b) => {
      if (a.sortKey !== b.sortKey) {
        // desc = newest first (b - a), asc = oldest first (a - b)
        return normalizedConfig.direction === "desc"
          ? b.sortKey - a.sortKey
          : a.sortKey - b.sortKey;
      }
      // Fallback to original order for ties
      return a.orderIndex - b.orderIndex;
    });
  }

  return displayItems;
}

/**
 * @deprecated Use groupParallelStudies instead
 */
export function groupStudiesByBatch(studies: Study[]): GroupedStudiesWithBatches {
  const result = groupParallelStudies(studies);
  return {
    ...result,
    // Add legacy property names
    batches: result.parallelGroups,
    batchCount: result.parallelGroupCount,
  };
}

/**
 * Check if a study is part of a parallel study group.
 */
export function isPartOfParallelGroup(study: Study): boolean {
  return study.batch_id !== undefined && study.batch_id !== null;
}

/**
 * @deprecated Use isPartOfParallelGroup instead
 */
export function isPartOfBatch(study: Study): boolean {
  return isPartOfParallelGroup(study);
}

/**
 * Get the parallel study version indicator string (e.g., "v1/3", "v2/3").
 */
export function getParallelVersionIndicator(study: Study): string | null {
  if (!study.batch_id || !study.batch_index || !study.batch_total) {
    return null;
  }
  return `v${study.batch_index}/${study.batch_total}`;
}

/**
 * @deprecated Use getParallelVersionIndicator instead
 */
export function getBatchIndicator(study: Study): string | null {
  return getParallelVersionIndicator(study);
}
